import { useState } from "react";
import { Link, useParams } from "wouter";
import { Award, Download, Printer, Shield, XCircle } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, PrimaryButton } from "@/components/Card";
import { certificateTemplate } from "@/data";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidate,
  useDictamenForExpediente,
  useEffectiveUser,
  useExpediente,
  useStandard,
  useUsersByRole,
} from "@/store/hooks";

export default function Certificate() {
  const params = useParams<{ expedienteId: string }>();
  const exp = useExpediente(params.expedienteId);
  const cand = useCandidate(exp?.candidatoId);
  const standard = useStandard(exp?.estandar);
  const dictamen = useDictamenForExpediente(exp?.id);
  const revocar = useDemoStore((s) => s.revocarCertificado);
  const dictaminadores = useUsersByRole("dictaminador");
  const effective = useEffectiveUser();
  const dictaminador = effective?.roles.includes("dictaminador") ? effective : dictaminadores[0];

  const [showRevoke, setShowRevoke] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (!exp || !cand) {
    return (
      <AppLayout breadcrumb={["Dictaminador", "Certificado"]}>
        <Card><div style={{ padding: 20, textAlign: "center", color: "var(--cc-gray-500)" }}>Expediente no encontrado.</div></Card>
      </AppLayout>
    );
  }

  const folio = exp.certificado.folio ?? "—";
  const fecha = exp.certificado.fechaEmision ?? "—";
  const emitido = exp.certificado.emitido;
  const revocado = !!dictamen?.revocacion;
  const estandarLabel = standard ? `${standard.codigo} — ${standard.titulo}` : exp.estandar;
  const vigencia = standard?.vigencia ?? certificateTemplate.vigencia;

  function handleRevoke() {
    setError(null);
    if (!dictaminador) { setError("No hay un dictaminador activo."); return; }
    const res = revocar(exp!.id, dictaminador.id, motivo);
    if (!res.ok) { setError(res.error ?? "No se pudo revocar."); return; }
    setShowRevoke(false);
    setMotivo("");
  }

  return (
    <AppLayout breadcrumb={["Dictaminador", "Certificado emitido"]}>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area { position: absolute !important; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Certificado de competencia</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            Folio {folio} · Emitido {fecha} {revocado && <span style={{ color: "var(--cc-red)", fontWeight: 700 }}>· REVOCADO</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <PrimaryButton variant="outline" testId="button-print" onClick={() => window.print()}>
            <Printer size={13} /> Imprimir
          </PrimaryButton>
          <PrimaryButton testId="button-download" onClick={() => window.print()}>
            <Download size={13} /> Descargar PDF
          </PrimaryButton>
          {emitido && !revocado && (
            <PrimaryButton variant="outline" testId="button-revoke" onClick={() => setShowRevoke(true)}>
              <XCircle size={13} /> Revocar
            </PrimaryButton>
          )}
        </div>
      </div>

      {showRevoke && (
        <div className="no-print" style={{ marginBottom: 14 }}>
        <Card style={{ border: "1px solid var(--cc-red)", background: "var(--cc-red-bg)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-red)", marginBottom: 8 }}>Revocar certificado {folio}</div>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo de la revocación (obligatorio, mínimo 5 caracteres)..."
            data-testid="textarea-revoke-motivo"
            style={{ width: "100%", minHeight: 80, padding: 10, border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }}
          />
          {error && <div style={{ marginTop: 8, fontSize: 12, color: "var(--cc-red)", fontWeight: 600 }} data-testid="text-revoke-error">{error}</div>}
          <div style={{ marginTop: 10, display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <PrimaryButton variant="outline" testId="button-cancel-revoke" onClick={() => { setShowRevoke(false); setError(null); }}>Cancelar</PrimaryButton>
            <PrimaryButton testId="button-confirm-revoke" onClick={handleRevoke}>Confirmar revocación</PrimaryButton>
          </div>
        </Card>
        </div>
      )}

      {revocado && (
        <div className="no-print" style={{ marginBottom: 14 }}>
        <Card style={{ border: "1px solid var(--cc-red)", background: "var(--cc-red-bg)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-red)" }}>Certificado revocado</div>
          <div style={{ fontSize: 12, color: "var(--cc-gray-700)", marginTop: 4 }}>
            Fecha: {dictamen!.revocacion!.fecha.slice(0, 10)} · Motivo: {dictamen!.revocacion!.motivo}
          </div>
        </Card>
        </div>
      )}

      <div className="print-area">
        <Card style={{ background: "linear-gradient(135deg, #fefce8 0%, #f0fdf4 100%)", border: "2px solid #d97706" }}>
          <div style={{ textAlign: "center", padding: "30px 20px", background: "white", border: "1px solid var(--cc-gray-200)", borderRadius: 12, position: "relative" }}>
            {revocado && (
              <div style={{ position: "absolute", top: 30, right: 30, transform: "rotate(15deg)", border: "4px solid var(--cc-red)", color: "var(--cc-red)", padding: "6px 16px", fontSize: 20, fontWeight: 800, letterSpacing: "0.1em" }}>REVOCADO</div>
            )}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg, var(--cc-navy), var(--cc-teal))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Award size={32} color="white" strokeWidth={2.2} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>{certificateTemplate.emisor}</div>
            <div style={{ fontSize: 14, color: "var(--cc-gray-700)", fontWeight: 600 }}>{certificateTemplate.entidad}</div>

            <div style={{ margin: "30px 0", fontSize: 17, color: "var(--cc-gray-600)", lineHeight: 1.7 }}>
              Otorga el presente <strong>CERTIFICADO DE COMPETENCIA</strong> a
            </div>

            <div style={{ fontSize: 30, fontWeight: 800, color: "var(--cc-navy)", letterSpacing: "-0.02em", marginBottom: 4 }} data-testid="text-cert-name">
              {cand.nombre}
            </div>
            <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{cand.unidad} · CURP {cand.curp}</div>

            <div style={{ margin: "30px auto", maxWidth: 680, fontSize: 14, color: "var(--cc-gray-700)", lineHeight: 1.7 }}>
              Por haber demostrado las habilidades, conocimientos y actitudes definidos en el estándar de competencia
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--cc-navy)", margin: "10px 0" }}>{estandarLabel}</div>
              con vigencia de {vigencia} a partir de la fecha de emisión.
            </div>

            <div style={{ display: "flex", justifyContent: "space-around", marginTop: 36, padding: "0 40px" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ borderTop: "1px solid var(--cc-gray-400)", width: 200, margin: "0 auto 6px" }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{dictaminador?.nombre ?? "Dictaminador académico"}</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Dictaminador académico {dictaminador?.cedula ? `· ${dictaminador.cedula}` : ""}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ borderTop: "1px solid var(--cc-gray-400)", width: 200, margin: "0 auto 6px" }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>Lic. Adriana Cruz Robledo</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Representante CertiCore</div>
              </div>
            </div>

            <div style={{ marginTop: 26, display: "flex", justifyContent: "center", gap: 18, alignItems: "center" }}>
              <QRMock value={folio} />
              <div style={{ padding: "10px 16px", background: "var(--cc-gray-50)", borderRadius: 8 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "var(--cc-gray-600)", textAlign: "left" }}>
                  <div><Shield size={11} style={{ marginRight: 4, verticalAlign: "middle" }} /> Folio: <strong style={{ color: "var(--cc-navy)" }} data-testid="text-cert-folio">{folio}</strong></div>
                  <div>Fecha de emisión: <strong>{fecha}</strong></div>
                  <div>Verificación: <strong>certicore.mx/v/{folio}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="no-print" style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Link href="/dictaminador/historial" data-testid="link-historial">
          <PrimaryButton variant="outline">Ver historial</PrimaryButton>
        </Link>
        <Link href="/dictaminador" data-testid="link-back-bandeja">
          <PrimaryButton>Volver a la bandeja</PrimaryButton>
        </Link>
      </div>
    </AppLayout>
  );
}

function QRMock({ value }: { value: string }) {
  // Deterministic checker pattern from folio string.
  const size = 80;
  const cells = 8;
  const cellSize = size / cells;
  let h = 0;
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0;
  const filled: boolean[] = [];
  for (let i = 0; i < cells * cells; i++) {
    h = (h * 1103515245 + 12345) >>> 0;
    filled.push((h & 1) === 1);
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="QR simulado">
      <rect width={size} height={size} fill="white" stroke="var(--cc-navy)" strokeWidth={1} />
      {filled.map((on, i) => {
        if (!on) return null;
        const x = (i % cells) * cellSize;
        const y = Math.floor(i / cells) * cellSize;
        return <rect key={i} x={x + 1} y={y + 1} width={cellSize - 2} height={cellSize - 2} fill="var(--cc-navy)" />;
      })}
    </svg>
  );
}
